import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { categoryAPI } from '../services/api'
import { ArrowLeft, Upload, AlertCircle } from 'lucide-react'
import CategoryDropdown from '../components/CategoryDropdown'

const ImportFromLegacyPage = () => {
  const navigate = useNavigate()
  const [error, setError] = useState(null)

  const [formData, setFormData] = useState({
    category_id: '',
    subcategory_id: '',
    oldtemplate_id: ''
  })

  const [errors, setErrors] = useState({})
  const [importing, setImporting] = useState(false)


  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    
    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
    
    // Clear subcategory when category changes
    if (name === 'category_id') {
      setFormData(prev => ({ ...prev, subcategory_id: '' }))
    }
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setImporting(true)
    setErrors({})

    try {
      const response = await fetch('/api/v1/templates/import-from-legacy/addv1', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          category_id: formData.category_id,
          sub_category_id: formData.subcategory_id || null,
          old_template_id: formData.oldtemplate_id
        })
      })

      const result = await response.json()

      if (response.ok && result.success) {
        // Show success notification
        window.dispatchEvent(new CustomEvent('app:notify', {
          detail: {
            type: 'success',
            message: result.message || 'Template imported successfully!'
          }
        }))

        // Navigate back to dashboard
        navigate('/dashboard')
      } else {
        // Handle API errors
        const errorMessage = result.detail || result.message || 'Failed to import template'
        
        if (response.status === 409) {
          // Template ID already exists
          setErrors({ oldtemplate_id: errorMessage })
        } else {
          // Other errors
          window.dispatchEvent(new CustomEvent('app:notify', {
            detail: {
              type: 'error',
              message: errorMessage
            }
          }))
        }
      }
    } catch (error) {
      console.error('Import error:', error)
      window.dispatchEvent(new CustomEvent('app:notify', {
        detail: {
          type: 'error',
          message: 'Network error. Please check your connection and try again.'
        }
      }))
    } finally {
      setImporting(false)
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.category_id) {
      newErrors.category_id = 'Category is required'
    }

    if (!formData.oldtemplate_id.trim()) {
      newErrors.oldtemplate_id = 'Template ID is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Import from Legacy Platform</h1>
          <p className="text-gray-600 mt-2">
            Import your existing templates from the legacy platform by providing the template ID and selecting the appropriate category.
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <AlertCircle className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-red-800 font-medium">Error</h3>
              <p className="text-red-700 text-sm mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Upload className="w-5 h-5 mr-2 text-blue-600" />
              Import Details
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-6">
              {/* Category Selection */}
              <div className="grid grid-cols-1 gap-6">
                <CategoryDropdown
                  value={formData.category_id}
                  onChange={(value) => setFormData(prev => ({ ...prev, category_id: value, subcategory_id: '' }))}
                  placeholder="Select a category"
                  type="template"
                  isActive={true}
                  required={true}
                  label="Category"
                  error={errors.category_id}
                  showSubcategories={true}
                  subcategoryValue={formData.subcategory_id}
                  onSubcategoryChange={(value) => setFormData(prev => ({ ...prev, subcategory_id: value }))}
                  subcategoryPlaceholder="Select a subcategory (optional)"
                />
              </div>

              {/* Old Template ID */}
              <div>
                <label htmlFor="oldtemplate_id" className="block text-sm font-medium text-gray-700 mb-1">
                  Template ID *
                </label>
                <input
                  type="text"
                  id="oldtemplate_id"
                  name="oldtemplate_id"
                  value={formData.oldtemplate_id}
                  onChange={handleInputChange}
                  placeholder="Enter the template ID from your legacy platform"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.oldtemplate_id ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.oldtemplate_id && <p className="text-sm text-red-600 mt-1">{errors.oldtemplate_id}</p>}
                <p className="text-sm text-gray-500 mt-1">
                  Enter the unique identifier of your template from the legacy system. The system will automatically fetch and process the template data.
                </p>
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-8 flex justify-end">
              <button
                type="submit"
                disabled={importing}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {importing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import Template
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ImportFromLegacyPage
